import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Nav, Row, Col } from "react-bootstrap";
import { FaEdit, FaTimes, FaSave, FaCalculator, FaMoneyBillWave, FaClock, FaCheckCircle } from "react-icons/fa";
import { API } from "../../config/api";
import swal from "sweetalert";

// Helper format rupiah
const formatRupiah = (number) => {
  if (number === null || number === undefined || isNaN(number)) return "Rp 0";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(number);
};

export default function EditSalarySlipModal({ show, handleClose, data, onSuccess }) {
  const [activeTab, setActiveTab] = useState("gaji");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  const token = localStorage.getItem("token");
  const fetchParams = {
    headers: {
      Authorization: `${token}`,
      "Content-Type": "application/json",
    },
  };

  useEffect(() => {
    if (data) {
      setFormData({
        id: data.id,
        nama: data.nama || "",
        nip_ypi: data.nip_ypi || "",
        nip_ysb: data.nip_ysb || "",
        periode: data.periode || "",
        no_rek_bni: data.no_rek_bni || "",
        unit_sekolah: data.unit_sekolah || data.school?.school_name || data.ysb_school_id || "",
        tgl_transfer: data.tgl_transfer || data.transfer || "",
        
        // Gaji & Tunjangan Tetap
        gaji_pokok: data.gaji_pokok || 0,
        tunj_pengabdian: data.tunj_pengabdian || 0,
        tunj_keluarga: data.tunj_keluarga || 0,
        tunj_yayasan: data.tunj_yayasan || 0,
        tunj_jabatan: data.tunj_jabatan || 0,
        tunj_daerah: data.tunj_daerah || 0,
        tunj_pph21: data.tunj_pph21 || 0,
        tunj_bpjs_ket: data.tunj_bpjs_ket || 0,
        tunj_bpjs_kes: data.tunj_bpjs_kes || 0,
        
        // Insentif & Variabel
        ukk: data.ukk || 0,
        nilai_kinerja: data.nilai_kinerja !== undefined ? data.nilai_kinerja : 1,
        ukk_kinerja: data.ukk_kinerja || 0,
        u_transport: data.u_transport || 0,
        u_makan: data.u_makan || 0,
        insentif_idk_kjm: data.insentif_idk_kjm || 0,
        kord_bidang: data.kord_bidang || 0,
        wali_kelas: data.wali_kelas || 0,
        ragam_tunj_lainnya: data.ragam_tunj_lainnya || 0,
        pembina_ekskul_ket: data.pembina_ekskul_ket || data.pembina_sd_ket || "",
        pembina_ekskul_nom: data.pembina_ekskul_nom || data.pembina_sd_nom || 0,
        pendapatan_lainnya: data.pendapatan_lainnya || data.rekap_lainnya || 0,
        
        // Potongan
        pot_ketenagakerjaan: data.pot_ketenagakerjaan || 0,
        pot_kesehatan: data.pot_kesehatan || 0,
        pot_infaq_pegawai: data.pot_infaq_pegawai || data.pot_zakat || 0,
        pot_koperasi: data.pot_koperasi || 0,
        pot_ukk: data.pot_ukk || data.pot_absensi || 0,
        pot_wfh_transport: data.pot_wfh_transport || 0,
        pot_pph21: data.pot_pph21 || 0,
        pot_bpjs_ket_subsidi: data.pot_bpjs_ket_subsidi || 0,
        pot_bpjs_kes_subsidi: data.pot_bpjs_kes_subsidi || 0,
        pot_infaq_masjid: data.pot_infaq_masjid || 0,
        
        // Data Absensi
        att_terlambat_kurang_5: data.att_terlambat_kurang_5 ?? 0,
        att_terlambat_lebih_5: data.att_terlambat_lebih_5 ?? 0,
        att_pulang_kurang_5: data.att_pulang_kurang_5 ?? 0,
        att_pulang_lebih_5: data.att_pulang_lebih_5 ?? 0,
        att_absen_1x: data.att_absen_1x ?? 0,
        att_hadir_kerja: data.att_hadir_kerja ?? 0,
        att_hadir_libur: data.att_hadir_libur ?? 0,
        att_jml_hadir: data.att_jml_hadir ?? 0,
        att_tidak_hadir: data.att_tidak_hadir ?? 0,
        att_jml_kjm: data.att_jml_kjm ?? 0,
      });
    }
  }, [data]);

  if (!data) return null;

  // Live Auto Calculation
  const numVal = (val) => {
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  };

  const calcSubtotalGaji =
    numVal(formData.gaji_pokok) +
    numVal(formData.tunj_pengabdian) +
    numVal(formData.tunj_keluarga) +
    numVal(formData.tunj_yayasan) +
    numVal(formData.tunj_jabatan) +
    numVal(formData.tunj_daerah) +
    numVal(formData.tunj_pph21) +
    numVal(formData.tunj_bpjs_ket) +
    numVal(formData.tunj_bpjs_kes);

  const calcSubtotalInsentif =
    numVal(formData.ukk) +
    numVal(formData.ukk_kinerja) +
    numVal(formData.u_transport) +
    numVal(formData.u_makan) +
    numVal(formData.insentif_idk_kjm) +
    numVal(formData.kord_bidang) +
    numVal(formData.wali_kelas) +
    numVal(formData.ragam_tunj_lainnya) +
    numVal(formData.pembina_ekskul_nom) +
    numVal(formData.pendapatan_lainnya);

  const calcTotalPendapatan = calcSubtotalGaji + calcSubtotalInsentif;

  const calcTotalPotongan =
    numVal(formData.pot_ketenagakerjaan) +
    numVal(formData.pot_kesehatan) +
    numVal(formData.pot_infaq_pegawai) +
    numVal(formData.pot_koperasi) +
    numVal(formData.pot_ukk) +
    numVal(formData.pot_wfh_transport) +
    numVal(formData.pot_pph21) +
    numVal(formData.pot_bpjs_ket_subsidi) +
    numVal(formData.pot_bpjs_kes_subsidi) +
    numVal(formData.pot_infaq_masjid);

  const calcDitransfer = calcTotalPendapatan - calcTotalPotongan;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        jumlah_gaji: calcSubtotalGaji,
        rekap_gaji: calcSubtotalGaji,
        jumlah_insentif: calcSubtotalInsentif,
        rekap_insentif: calcSubtotalInsentif,
        total_pendapatan: calcTotalPendapatan,
        jumlah_potongan: calcTotalPotongan,
        rekap_potongan: calcTotalPotongan,
        ditransfer: calcDitransfer,
      };

      const response = await API.post(`/api/salary-slips/update/${data.id}`, payload, fetchParams);

      if (response?.status === 200) {
        swal({
          title: "Berhasil!",
          text: "Data slip gaji berhasil diperbarui.",
          icon: "success",
        });
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      swal({
        title: "Gagal Memperbarui",
        text: error?.response?.data?.message || "Terjadi kesalahan saat menyimpan data.",
        icon: "error",
      });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered scrollable>
      <Modal.Header closeButton style={{ backgroundColor: "#00487F", color: "white" }}>
        <Modal.Title style={{ fontSize: "16px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
          <FaEdit /> Edit Slip Gaji - {formData.nama} ({formData.periode})
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: "#f8fafc", padding: "20px" }}>
        {/* Ringkasan Live Calculation Banner */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px 18px",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase" }}>Total Pendapatan (A)</div>
              <div style={{ fontSize: "16px", fontWeight: "bold", color: "#166534" }}>{formatRupiah(calcTotalPendapatan)}</div>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase" }}>Total Potongan (B)</div>
              <div style={{ fontSize: "16px", fontWeight: "bold", color: "#991b1b" }}>{formatRupiah(calcTotalPotongan)}</div>
            </div>
            <div style={{ borderLeft: "2px solid #cbd5e1", paddingLeft: "16px" }}>
              <div style={{ fontSize: "11px", color: "#00487F", fontWeight: "bold", textTransform: "uppercase" }}>
                Penghasilan Bersih (Ditransfer)
              </div>
              <div style={{ fontSize: "18px", fontWeight: "bold", color: "#00487F" }}>{formatRupiah(calcDitransfer)}</div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3" style={{ gap: "6px" }}>
          <Nav.Item>
            <Nav.Link eventKey="gaji" style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
              1. Gaji & Tunjangan Tetap
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="insentif" style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
              2. Insentif & Variabel
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="potongan" style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
              3. Potongan
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="absensi" style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
              4. Data Absensi
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Tab Content */}
        <div style={{ backgroundColor: "#ffffff", padding: "18px 20px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          {/* TAB 1: GAJI & TUNJANGAN TETAP */}
          {activeTab === "gaji" && (
            <div>
              <h6 style={{ fontWeight: "bold", color: "#00487F", marginBottom: "14px" }}>
                Edit Gaji & Tunjangan Tetap (Subtotal: {formatRupiah(calcSubtotalGaji)})
              </h6>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Gaji Pokok (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.gaji_pokok}
                      onChange={(e) => handleInputChange("gaji_pokok", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tunj. Pengabdian (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.tunj_pengabdian}
                      onChange={(e) => handleInputChange("tunj_pengabdian", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tunj. Keluarga (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.tunj_keluarga}
                      onChange={(e) => handleInputChange("tunj_keluarga", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tunj. Yayasan (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.tunj_yayasan}
                      onChange={(e) => handleInputChange("tunj_yayasan", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tunj. Jabatan (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.tunj_jabatan}
                      onChange={(e) => handleInputChange("tunj_jabatan", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tunj. Daerah (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.tunj_daerah}
                      onChange={(e) => handleInputChange("tunj_daerah", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tunj. PPh 21 (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.tunj_pph21}
                      onChange={(e) => handleInputChange("tunj_pph21", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tunj. BPJS Ket. Subsidi (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.tunj_bpjs_ket}
                      onChange={(e) => handleInputChange("tunj_bpjs_ket", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tunj. BPJS Kes. Subsidi (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.tunj_bpjs_kes}
                      onChange={(e) => handleInputChange("tunj_bpjs_kes", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          {/* TAB 2: INSENTIF & VARIABEL */}
          {activeTab === "insentif" && (
            <div>
              <h6 style={{ fontWeight: "bold", color: "#00487F", marginBottom: "14px" }}>
                Edit Insentif & Tunjangan Variabel (Subtotal: {formatRupiah(calcSubtotalInsentif)})
              </h6>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>UKK (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.ukk}
                      onChange={(e) => handleInputChange("ukk", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Nilai Kinerja (%)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formData.nilai_kinerja}
                      onChange={(e) => handleInputChange("nilai_kinerja", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>UKK Kinerja (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.ukk_kinerja}
                      onChange={(e) => handleInputChange("ukk_kinerja", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Uang Transport (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.u_transport}
                      onChange={(e) => handleInputChange("u_transport", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Uang Makan (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.u_makan}
                      onChange={(e) => handleInputChange("u_makan", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Insentif IDK / KJM (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.insentif_idk_kjm}
                      onChange={(e) => handleInputChange("insentif_idk_kjm", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Kord. Bidang (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.kord_bidang}
                      onChange={(e) => handleInputChange("kord_bidang", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Wali Kelas (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.wali_kelas}
                      onChange={(e) => handleInputChange("wali_kelas", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Ragam Tunj. Lainnya (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.ragam_tunj_lainnya}
                      onChange={(e) => handleInputChange("ragam_tunj_lainnya", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Pendapatan Lainnya (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pendapatan_lainnya}
                      onChange={(e) => handleInputChange("pendapatan_lainnya", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Keterangan Pembina Ekskul</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Contoh: TAHFIDZ (Kelas 2 & 3)"
                      value={formData.pembina_ekskul_ket}
                      onChange={(e) => handleInputChange("pembina_ekskul_ket", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Nominal Pembina Ekskul (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pembina_ekskul_nom}
                      onChange={(e) => handleInputChange("pembina_ekskul_nom", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          {/* TAB 3: POTONGAN */}
          {activeTab === "potongan" && (
            <div>
              <h6 style={{ fontWeight: "bold", color: "#991b1b", marginBottom: "14px" }}>
                Edit Potongan Gaji (Total: {formatRupiah(calcTotalPotongan)})
              </h6>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>BPJS Ketenagakerjaan (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_ketenagakerjaan}
                      onChange={(e) => handleInputChange("pot_ketenagakerjaan", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>BPJS Kesehatan (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_kesehatan}
                      onChange={(e) => handleInputChange("pot_kesehatan", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Infaq Pegawai (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_infaq_pegawai}
                      onChange={(e) => handleInputChange("pot_infaq_pegawai", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Koperasi (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_koperasi}
                      onChange={(e) => handleInputChange("pot_koperasi", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Potongan UKK (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_ukk}
                      onChange={(e) => handleInputChange("pot_ukk", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Potongan WFH Transport (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_wfh_transport}
                      onChange={(e) => handleInputChange("pot_wfh_transport", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>PPh 21 (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_pph21}
                      onChange={(e) => handleInputChange("pot_pph21", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>BPJS Ket. Subsidi (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_bpjs_ket_subsidi}
                      onChange={(e) => handleInputChange("pot_bpjs_ket_subsidi", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>BPJS Kes. Subsidi (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_bpjs_kes_subsidi}
                      onChange={(e) => handleInputChange("pot_bpjs_kes_subsidi", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Infaq Masjid (Rp)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.pot_infaq_masjid}
                      onChange={(e) => handleInputChange("pot_infaq_masjid", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          {/* TAB 4: DATA ABSENSI */}
          {activeTab === "absensi" && (
            <div>
              <h6 style={{ fontWeight: "bold", color: "#334155", marginBottom: "14px" }}>
                Edit Data Absensi
              </h6>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Terlambat -5 (kali)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_terlambat_kurang_5}
                      onChange={(e) => handleInputChange("att_terlambat_kurang_5", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Terlambat +5 (kali)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_terlambat_lebih_5}
                      onChange={(e) => handleInputChange("att_terlambat_lebih_5", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Pulang Cepat -5 (kali)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_pulang_kurang_5}
                      onChange={(e) => handleInputChange("att_pulang_kurang_5", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Pulang Cepat +5 (kali)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_pulang_lebih_5}
                      onChange={(e) => handleInputChange("att_pulang_lebih_5", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Absen 1x (kali)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_absen_1x}
                      onChange={(e) => handleInputChange("att_absen_1x", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Hadir Kerja (hari)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_hadir_kerja}
                      onChange={(e) => handleInputChange("att_hadir_kerja", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Hadir Libur (hari)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_hadir_libur}
                      onChange={(e) => handleInputChange("att_hadir_libur", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Jml Hadir (hari)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_jml_hadir}
                      onChange={(e) => handleInputChange("att_jml_hadir", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "12.5px", fontWeight: "600" }}>Tidak Hadir (hari)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.att_tidak_hadir}
                      onChange={(e) => handleInputChange("att_tidak_hadir", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ backgroundColor: "#ffffff" }}>
        <Button variant="secondary" onClick={handleClose} disabled={loading} style={{ borderRadius: "6px", fontSize: "13px" }}>
          <FaTimes className="me-1" /> Batal
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            backgroundColor: "#00487F",
            borderColor: "#00487F",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
          }}
        >
          <FaSave className="me-1" /> {loading ? "Menyimpan..." : "Simpan Perubahan"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
